import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * ─── Select ────────────────────────────────────────────────────────────────
 * Custom accessible select. Replaces every native <select> (PDR v2.1 §8).
 *
 * Props
 *   value       controlled value (any primitive)
 *   onChange    (nextValue) => void
 *   options     Array<{ value, label, description?, disabled? }>
 *   placeholder string
 *   error       boolean
 *   disabled    boolean
 *   testId
 *
 * Accessibility
 *   - role="combobox" trigger with aria-expanded / aria-haspopup="listbox".
 *   - role="listbox" menu with role="option" children.
 *   - Keyboard: Space/Enter opens menu; ArrowUp/Down navigates; Enter selects;
 *     Escape closes.
 */
export default function Select({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  error = false,
  disabled = false,
  className = "",
  testId,
  ariaLabel,
  ariaLabelledBy,
  "aria-labelledby": ariaLabelledByHtml,
  id,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const listboxId = useId();

  const selectedIdx = options.findIndex(o => o.value === value);
  const selected    = selectedIdx >= 0 ? options[selectedIdx] : null;

  const close = useCallback(() => { setOpen(false); setActiveIdx(-1); }, []);
  const openMenu = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
  }, [disabled, selectedIdx]);

  // outside click / escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) close(); };
    const onKey  = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const move = (delta) => {
    let i = activeIdx;
    for (let n = 0; n < options.length; n++) {
      i = (i + delta + options.length) % options.length;
      if (!options[i].disabled) { setActiveIdx(i); return; }
    }
  };

  const onTriggerKeyDown = (e) => {
    if (["Enter", " ", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      open ? move(1) : openMenu();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      open ? move(-1) : openMenu();
    } else if (e.key === "Enter" && open && activeIdx >= 0) {
      e.preventDefault();
      onChange?.(options[activeIdx].value); close();
    }
  };

  const commit = (v) => { onChange?.(v); close(); };

  const wrapCn = ["ds-select", open && "ds-select--open", className].filter(Boolean).join(" ");
  // Accessible name fallback so axe `button-name` passes even without a wrapping FormField.
  const accessibleName =
    ariaLabel
    || (selected ? `${selected.label}` : placeholder && placeholder !== "Select…" ? placeholder : undefined);

  return (
    <div ref={wrapRef} className={wrapCn} data-testid={testId}>
      <button
        type="button"
        id={id}
        className="ds-select__trigger"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={(ariaLabelledBy || ariaLabelledByHtml) ? undefined : (accessibleName || "Select an option")}
        aria-labelledby={ariaLabelledBy || ariaLabelledByHtml}
        aria-invalid={error || undefined}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`ds-select__value ${selected ? "" : "ds-select__value--placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} strokeWidth={2} className="ds-select__caret" />
      </button>
      {open && (
        <ul id={listboxId} role="listbox" className="ds-select__menu"
            data-testid={testId ? `${testId}-menu` : undefined}>
          {options.map((opt, i) => (
            <li key={String(opt.value)} role="option"
                aria-selected={opt.value === value}>
              <button
                type="button"
                className="ds-select__option"
                data-active={i === activeIdx || undefined}
                data-selected={opt.value === value || undefined}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => commit(opt.value)}
                disabled={opt.disabled}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                {opt.value === value
                  ? <Check size={14} strokeWidth={2.4} />
                  : <span style={{ width: 14, height: 14, display: "inline-block" }} />}
                <span style={{ flex: 1, textAlign: "left" }}>{opt.label}</span>
                {opt.description && (
                  <span style={{ fontSize: 11, color: "var(--hbf-text-subtle)" }}>{opt.description}</span>
                )}
              </button>
            </li>
          ))}
          {options.length === 0 && (
            <li className="ds-select__option" style={{ color: "var(--hbf-text-subtle)", cursor: "default" }}>
              No options
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
